import { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import {
  getEmailFollowUps,
  getThreadFollowUpStatus,
  enrollThreadFollowUp,
  stopEmailFollowUpRun,
} from '../../api';
import { Modal, Button } from '@/components';

export function ThreadFollowUpWidget({ companyId, threadId }) {
  const [statusInfo, setStatusInfo] = useState(null);
  const [sequences, setSequences] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedSeqId, setSelectedSeqId] = useState('');
  const [isEnrolling, setIsEnrolling] = useState(false);

  const fetchStatus = useCallback(async () => {
    if (!companyId || !threadId) return;
    try {
      const data = await getThreadFollowUpStatus(companyId, threadId);
      setStatusInfo(data);
    } catch (err) {
      console.error('Error fetching thread follow-up status', err);
    }
  }, [companyId, threadId]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const handleOpenEnrollModal = async () => {
    try {
      const list = await getEmailFollowUps(companyId);
      const activeList = (list.results || list || []).filter(
        (s) => s.is_active,
      );
      setSequences(activeList);
      if (activeList.length > 0) {
        setSelectedSeqId(activeList[0].id);
      }
      setShowModal(true);
    } catch {
      toast.error('Failed fetching available follow-up sequences');
    }
  };

  const handleEnroll = async () => {
    if (!selectedSeqId) {
      toast.error('Please select a sequence');
      return;
    }
    setIsEnrolling(true);
    try {
      await enrollThreadFollowUp(companyId, threadId, selectedSeqId);
      toast.success('Thread enrolled in follow-up sequence!');
      setShowModal(false);
      fetchStatus();
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || 'Failed enrolling thread in follow-up',
      );
    } finally {
      setIsEnrolling(false);
    }
  };

  const handleStop = async (runId) => {
    try {
      await stopEmailFollowUpRun(companyId, runId);
      toast.success('Follow-up stopped');
      fetchStatus();
    } catch {
      toast.error('Failed stopping follow-up');
    }
  };

  if (!companyId || !threadId) return null;

  const run = statusInfo?.run;
  const isActive = statusInfo?.active;

  return (
    <div className="flex items-center gap-2">
      {isActive && run ? (
        <div className="flex items-center gap-2 bg-layer-2 border border-border-default rounded-xl px-3 py-1.5 text-xs">
          <span className="flex items-center gap-1 font-semibold text-primary">
            <svg
              className="w-3 h-3 text-primary"
              fill="currentColor"
              viewBox="0 0 24 24"
            >
              <polygon points="5 3 19 12 5 21 5 3" />
            </svg>
            {statusInfo.sequence_name || 'Follow-Up'}
          </span>
          <span className="text-text-muted">|</span>
          <span className="text-text-secondary">
            Step {statusInfo.current_step_order || 1} of{' '}
            {statusInfo.total_steps || 1}
          </span>
          {run.next_run_at && (
            <>
              <span className="text-text-muted">|</span>
              <span className="flex items-center gap-1 text-status-success font-medium">
                <svg
                  className="w-3 h-3 text-status-success"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <circle cx="12" cy="12" r="10" />
                  <polyline points="12 6 12 12 16 14" />
                </svg>
                {new Date(run.next_run_at).toLocaleString([], {
                  month: 'short',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </span>
            </>
          )}
          <button
            onClick={() => handleStop(run.id)}
            className="ml-1 p-1 text-text-muted hover:text-status-error hover:bg-status-error/10 rounded transition"
            title="Stop Follow-Up"
          >
            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
              <rect x="6" y="6" width="12" height="12" />
            </svg>
          </button>
        </div>
      ) : (
        <Button
          variant="secondary"
          size="sm"
          onClick={handleOpenEnrollModal}
          className="h-8 px-3 text-xs"
        >
          <svg
            className="w-3.5 h-3.5 mr-1"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M12 4v16m8-8H4"
            />
          </svg>
          Add Follow-Up
        </Button>
      )}

      {/* Enroll Modal */}
      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={
          <span className="flex items-center gap-2">
            <svg
              className="w-5 h-5 text-primary"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 4v16m8-8H4"
              />
            </svg>
            Enroll Thread in Follow-Up
          </span>
        }
        size="md"
        footer={
          <>
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              Cancel
            </Button>
            {sequences.length > 0 && (
              <Button onClick={handleEnroll} disabled={isEnrolling}>
                {isEnrolling ? 'Enrolling...' : 'Start Follow-Up'}
              </Button>
            )}
          </>
        }
      >
        <div className="flex flex-col gap-4">
          {sequences.length === 0 ? (
            <p className="text-sm text-text-secondary">
              No active follow-up sequences defined. Create a sequence first in
              the Follow-Up builder.
            </p>
          ) : (
            <div>
              <label className="block text-sm font-medium text-text-primary mb-1.5">
                Select Sequence
              </label>
              <select
                value={selectedSeqId}
                onChange={(e) => setSelectedSeqId(e.target.value)}
                className="w-full bg-layer-2 border border-border-default rounded-xl p-2.5 text-text-primary text-sm focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-all"
              >
                {sequences.map((seq) => (
                  <option key={seq.id} value={seq.id}>
                    {seq.name} ({seq.steps?.length || 0} steps)
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
}
